'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';

export type PayoutCell = string | { main: string; sub?: string };

export interface PayoutColumn {
  header: string;
  cells: Record<string, PayoutCell>;
}

export interface PayoutPackageContent {
  type: 'package';
  rows: string[];
  columns: PayoutColumn[];
  visibleColumns?: number;
}

export interface PayoutIBoxSubTab {
  /** Big / Small / Prize, etc. */
  label: string;
  rows: string[];
  columns: PayoutColumn[];
  /** Defaults to 2. */
  visibleColumns?: number;
}

export interface PayoutIBoxContent {
  type: 'ibox';
  subTabs: PayoutIBoxSubTab[];
}

export type PayoutTabContent = PayoutPackageContent | PayoutIBoxContent;

export interface PayoutTab {
  id: string;
  label: string;
  content: PayoutTabContent;
}

// Shared building blocks
const HEADER_ROW = 'h-10';
const DATA_ROW = 'h-12';

function totalPages(count: number, perPage: number) {
  return Math.max(1, Math.ceil(count / Math.max(1, perPage)));
}

function Dots({ total, current }: { total: number; current: number }) {
  if (total <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-1.5">
      {Array.from({ length: total }, (_, i) => i).map(dotIdx => (
        <span
          key={dotIdx}
          className={`size-1.5 rounded-full transition-colors ${
            dotIdx === current ? 'bg-white' : 'bg-white/30'
          }`}
          aria-hidden
        />
      ))}
    </div>
  );
}

function CellContent({ value }: { value: PayoutCell | undefined }) {
  if (value === undefined || value === null || value === '' || value === '-') {
    return <span className="text-gray-500">-</span>;
  }
  if (typeof value === 'string') {
    return <span>{value}</span>;
  }
  return (
    <span className="flex flex-col items-center leading-tight">
      <span>{value.main}</span>
      {value.sub ? (
        <span className="text-[10px] text-gray-400">({value.sub})</span>
      ) : null}
    </span>
  );
}

function SlidingPanel({
  direction,
  viewKey,
  children,
  className,
}: {
  direction: 1 | -1;
  viewKey: string | number;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <AnimatePresence initial={false} mode="wait" custom={direction}>
      <motion.div
        key={viewKey}
        custom={direction}
        initial={{ x: direction * 32, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -direction * 32, opacity: 0 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className={className}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

// Renders the currently visible page of columns(header row + data cells).
function ColumnsPage({
  rows,
  columns,
  visibleColumns,
  page,
}: {
  rows: string[];
  columns: PayoutColumn[];
  visibleColumns: number;
  page: number;
}) {
  const start = page * visibleColumns;
  const slice = columns.slice(start, start + visibleColumns);
  const padded: (PayoutColumn | null)[] = [...slice];
  while (padded.length < visibleColumns) padded.push(null);

  const gridStyle = {
    gridTemplateColumns: `repeat(${visibleColumns}, minmax(0, 1fr))`,
  };
  const paddedEntries = padded.map((col, i) => ({
    col,
    key: col?.header ?? `__pad-${i}`,
  }));

  return (
    <div>
      <div className={`grid ${HEADER_ROW}`} style={gridStyle}>
        {paddedEntries.map(({ col, key }) => (
          <div
            key={key}
            className="flex items-center justify-center px-2 text-[11px] sm:text-sm text-gray-200 px-1"
          >
            {col?.header ?? ''}
          </div>
        ))}
      </div>
      <div className="overflow-hidden ">
        {rows.map((row, idx) => (
          <div
            key={row}
            className={`grid ${DATA_ROW} items-center text-center text-[11px] text-white ${
              idx % 2 === 0 ? 'bg-[#D9D9D9]/10' : 'bg-transparent'
            }`}
            style={gridStyle}
          >
            {paddedEntries.map(({ col, key }) => (
              <div key={key} className="px-2">
                {col ? (
                  <CellContent value={col.cells[row]} />
                ) : (
                  <span className="text-gray-500">-</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// Layouts
function PackageLayout({ data }: { data: PayoutPackageContent }) {
  const visible = data.visibleColumns ?? 3;
  const pages = totalPages(data.columns.length, visible);
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);

  const go = (dir: 1 | -1) => {
    const next = page + dir;
    if (next < 0 || next >= pages) return;
    setDirection(dir);
    setPage(next);
  };

  return (
    <div className="flex flex-col gap-3 pb-1">
      <div className="flex items-stretch gap-1 sm:gap-2">
        <PackageRowLabels rows={data.rows} />
        <div className="flex-1 flex flex-col gap-3 items-center">
          <div className="w-full overflow-hidden rounded-xl border border-white/30">
            <div className="relative   flex items-center gap-1.5">
              {/* <ArrowButton
              direction="left"
              onClick={() => go(-1)}
              disabled={page === 0}
            /> */}
              <button
                type="button"
                onClick={() => go(-1)}
                disabled={page === 0}
                aria-label={'Previous'}
                className="absolute top-2.5 left-1 z-50 flex size-5 shrink-0 items-center justify-center rounded-full border border-purple-500/40 bg-[#0f0a1f]/80 text-gray-300 transition-colors hover:bg-[#7E22CE]/40 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronLeft className="size-3" />
              </button>
              <div className="relative flex-1 overflow-hidden">
                <SlidingPanel direction={direction} viewKey={page}>
                  <ColumnsPage
                    rows={data.rows}
                    columns={data.columns}
                    visibleColumns={visible}
                    page={page}
                  />
                </SlidingPanel>
              </div>
              {/* <ArrowButton
              direction="right"
              onClick={() => go(1)}
              disabled={page >= pages - 1}
            /> */}
              <button
                type="button"
                onClick={() => go(1)}
                disabled={page >= pages - 1}
                aria-label={'Previous'}
                className="absolute top-2.5 right-1 z-50 flex size-5 shrink-0 items-center justify-center rounded-full border border-purple-500/40 bg-[#0f0a1f]/80 text-gray-300 transition-colors hover:bg-[#7E22CE]/40 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronRight className="size-3" />
              </button>
            </div>
          </div>
          <Dots total={pages} current={page} />
        </div>
      </div>
    </div>
  );
}

function PackageRowLabels({ rows }: { rows: string[] }) {
  return (
    <div className="flex w-20 shrink-0 flex-col sm:w-32">
      <div className={HEADER_ROW} aria-hidden />
      {rows.map(row => (
        <div
          key={row}
          className={`flex ${DATA_ROW} items-center px-2 text-xs  text-[#FFA035]`}
        >
          {row}
        </div>
      ))}
    </div>
  );
}

function IBoxLayout({ data }: { data: PayoutIBoxContent }) {
  const [subPage, setSubPage] = useState(0);
  const [colPage, setColPage] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);

  // Reset column page when sub-tab changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset col page when subPage changes
  useEffect(() => {
    setColPage(0);
  }, [subPage]);

  const sub = data.subTabs[subPage] ?? data.subTabs[0];
  const visible = sub.visibleColumns ?? 2;
  const colPages = totalPages(sub.columns.length, visible);

  const goSub = (dir: 1 | -1) => {
    const next = subPage + dir;
    if (next < 0 || next >= data.subTabs.length) return;
    setDirection(dir);
    setSubPage(next);
  };

  const goCol = (dir: 1 | -1) => {
    const next = colPage + dir;
    if (next < 0 || next >= colPages) return;
    setDirection(dir);
    setColPage(next);
  };

  return (
    <div className="flex flex-col  gap-3 pb-2">
      <div className="flex items-stretch gap-1 sm:gap-2">
        {/* Left side: sub-tab selector + row labels */}
        <div className="flex w-24 shrink-0 flex-col sm:w-32">
          <div
            className={`flex ${HEADER_ROW} items-center justify-between gap-1`}
          >
            {/* <ArrowButton
              direction="left"
              onClick={() => goSub(-1)}
              disabled={subPage === 0}
            /> */}
            <button
              type="button"
              onClick={() => goSub(-1)}
              disabled={subPage === 0}
              aria-label={'Previous'}
              className="z-50 flex size-5 shrink-0 items-center justify-center rounded-full border border-purple-500/40 bg-[#0f0a1f]/80 text-gray-300 transition-colors hover:bg-[#7E22CE]/40 disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronLeft className="size-3" />
            </button>
            <div className="flex-1 overflow-hidden text-center">
              <SlidingPanel direction={direction} viewKey={`label-${subPage}`}>
                <span className="block text-[13px]  text-white">
                  {sub.label}
                </span>
              </SlidingPanel>
            </div>
            {/* <ArrowButton
              direction="right"
              onClick={() => goSub(1)}
              disabled={subPage >= data.subTabs.length - 1}
            /> */}
            <button
              type="button"
              onClick={() => goSub(1)}
              disabled={subPage >= data.subTabs.length - 1}
              aria-label={'Next'}
              className="z-50 flex size-5 shrink-0 items-center justify-center rounded-full border border-purple-500/40 bg-[#0f0a1f]/80 text-gray-300 transition-colors hover:bg-[#7E22CE]/40 disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronRight className="size-3" />
            </button>
          </div>
          <div className="overflow-hidden">
            <SlidingPanel direction={direction} viewKey={`rows-${subPage}`}>
              {sub.rows.map(row => (
                <div
                  key={row}
                  className={`flex ${DATA_ROW} items-center px-2 text-xs font-medium text-amber-400`}
                >
                  {row}
                </div>
              ))}
            </SlidingPanel>
          </div>
          <div className="mt-1">
            <Dots total={data.subTabs.length} current={subPage} />
          </div>
        </div>

        {/* Right side: bordered table block */}
        <div className="flex-1 flex flex-col gap-1">
          <div className="flex  flex-col overflow-hidden rounded-2xl border border-white/50 ">
            <div className="flex items-center gap-1.5 relative">
              {/* <ArrowButton
              direction="left"
              onClick={() => goCol(-1)}
              disabled={colPage === 0}
            /> */}
              <button
                type="button"
                onClick={() => goCol(-1)}
                disabled={colPage === 0}
                aria-label={'Previous'}
                className="absolute top-2.5 left-1 z-50 flex size-5 shrink-0 items-center justify-center rounded-full border border-purple-500/40 bg-[#0f0a1f]/80 text-gray-300 transition-colors hover:bg-[#7E22CE]/40 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronLeft className="size-3" />
              </button>
              <div className="relative flex-1 overflow-hidden">
                <SlidingPanel
                  direction={direction}
                  viewKey={`table-${subPage}-${colPage}`}
                >
                  <ColumnsPage
                    rows={sub.rows}
                    columns={sub.columns}
                    visibleColumns={visible}
                    page={colPage}
                  />
                </SlidingPanel>
              </div>
              {/* <ArrowButton
              direction="right"
              onClick={() => goCol(1)}
              disabled={colPage >= colPages - 1}
            /> */}
              <button
                type="button"
                onClick={() => goCol(1)}
                disabled={colPage >= colPages - 1}
                aria-label={'Next'}
                className="absolute top-2.5 right-1 z-50 flex size-5 shrink-0 items-center justify-center rounded-full border border-purple-500/40 bg-[#0f0a1f]/80 text-gray-300 transition-colors hover:bg-[#7E22CE]/40 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronRight className="size-3" />
              </button>
            </div>
          </div>
          <Dots total={colPages} current={colPage} />
        </div>
      </div>
    </div>
  );
}

// Tab bar

function PayoutTabBar({
  tabs,
  activeId,
  onChange,
}: {
  tabs: PayoutTab[];
  activeId: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="-mx-1 bg-[#472258] flex gap-1 overflow-x-auto px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {tabs.map(tab => {
        const active = tab.id === activeId;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={`shrink-0 whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors sm:px-5 sm:text-sm ${
              active
                ? 'border border-purple-400 bg-[#1E1B4B]/60 text-white shadow-[0_0_0_1px_rgba(168,85,247,0.35)]'
                : 'border border-transparent text-gray-400 hover:text-gray-200'
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

// Main component

interface LotteryPayoutProps {
  tabs: PayoutTab[];
  className?: string;
}

export function LotteryPayout({ tabs, className }: LotteryPayoutProps) {
  const [activeId, setActiveId] = useState<string>(tabs[0]?.id ?? '');
  const active = tabs.find(t => t.id === activeId) ?? tabs[0];

  if (!active) {
    return (
      <div className="rounded-xl border border-[#5d2b7d]/50 bg-[#1E1B4B]/60 p-6 text-center text-xs text-gray-400">
        No payout information available.
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border border-purple-500/40 bg-[linear-gradient(135deg,#02062E_51%,#350C43_100%)] overflow-hidden pt-3  ${
        className ?? ''
      }`}
    >
      <PayoutTabBar tabs={tabs} activeId={activeId} onChange={setActiveId} />
      <div className="mt-2 px-2">
        {active.content.type === 'package' ? (
          <PackageLayout key={active.id} data={active.content} />
        ) : (
          <IBoxLayout key={active.id} data={active.content} />
        )}
      </div>
    </div>
  );
}
